import { captureModeFor, requiredSidesFor } from './documentTypes';

/**
 * Builds the multipart body posted directly to the IDV service.
 *
 * The captured `File` objects are attached as-is: no base64, no data URLs, no
 * re-encoding. Metadata is limited to capture geometry/quality — never PII and
 * never image content.
 */
export function buildDocumentFormData({ documentType, captures, captureSessionId }) {
  const sides = requiredSidesFor(documentType);
  const formData = new FormData();

  formData.append('capture_session_id', captureSessionId);
  formData.append('expected_document_type', documentType);
  formData.append('capture_mode', captureModeFor(documentType));

  const frameMetadata = [];

  sides.forEach((side) => {
    const frames = captures[side];
    if (!frames || !frames.length) return;

    const primary = frames[0];

    // 'passport' and 'front' both occupy the document_front slot; only a card
    // has a back.
    const field = side === 'back' ? 'document_back' : 'document_front';
    // No explicit filename: passing one makes FormData wrap the blob in a NEW
    // File. Omitting it keeps the captured File object itself, which is what
    // 'no re-encoding' actually means. File.name is used automatically.
    formData.append(field, primary.file);

    frames.forEach((frame) => frameMetadata.push(frame.metadata));
  });

  formData.append('frame_metadata_json', JSON.stringify({ frames: frameMetadata }));

  return formData;
}

/** Stable, single-use identifier binding one capture attempt to one upload. */
export function createCaptureSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `capture-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Maps an IDV staging response status to a user-facing message key.
 * Raw backend errors are never surfaced to the user.
 */
export function uploadErrorKey(status) {
  if (status === 401) return 'uploadExpired';
  if (status === 409) return 'uploadConflict';
  if (status === 429) return 'uploadRateLimited';

  return 'uploadFailed';
}
